import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'list-search-select-dialog',
  templateUrl: 'list-search-select-dialog.component.html',
  styleUrls: ["./list-search-select-dialog.component.scss"]
})
export class ListSearchSelectDialog<T> implements OnInit, OnDestroy {
  set filterText(filterText: string) {
    this._filterText = filterText;
    const normalizedFilterText: string = this.data.normalizeText ? this.data.normalizeText(filterText) : filterText;
    if(normalizedFilterText != this.normalizedFilterText) {
      this.normalizedFilterText = normalizedFilterText;
      this.applyFilter();
    }
  }

  get filterText(): string {
    return this._filterText;
  }

  private _filterText: string;
  private normalizedFilterText: string;
  private unconfirmedSelection: T;
  private filteredOptions: T[];
  private optionSubscription: Subscription;
  private loading: boolean;
  private loadingSubscription: Subscription;

  constructor(public dialogRef: MatDialogRef<ListSearchSelectDialog<T>>, public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: ListSearchSelectDialogData<T>) {
      this.filteredOptions = [];
      this.unconfirmedSelection = data.selector.getSelected();
      this.loading = data.loadingSubject != null;
  }

  ngOnInit() {
    this.optionSubscription = this.data.selector.options.subscribe(() => this.applyFilter());
    if(this.data.loadingSubject)
      this.loadingSubscription = this.data.loadingSubject.subscribe((loading: boolean) => this.loading = loading);
  }

  ngOnDestroy() {
    this.optionSubscription?.unsubscribe();
    this.loadingSubscription?.unsubscribe();
  }

  applyFilter() {
    this.filteredOptions = this.data.filter(this.data.selector.options.getValue() || [], this.filterText) || [];
    if(this.unconfirmedSelection != null && this.filteredOptions.findIndex((option: T): boolean => this.data.selector.optionsEqual(option, this.unconfirmedSelection)) < 0)
      this.select(null);
  }

  select(option: T) {
    this.unconfirmedSelection = option;
  }

  isSelected(option: T): boolean {
    return this.data.selector.optionsEqual(option, this.unconfirmedSelection);
  }

  isLoading(): boolean {
    return this.loading;
  }

  cancel() {
    this.dialogRef.close(null);
  }

  canConfirm(): boolean {
    return !this.loading && this.unconfirmedSelection && !this.data.selector.isSelected(this.unconfirmedSelection);
  }

  confirm() {
    if(!this.canConfirm())
      return;
    this.data.selector.select(this.unconfirmedSelection);
    this.dialogRef.close({
      value: this.unconfirmedSelection
    } as ListSearchSelectDialogResult<T>);
  }

  getEmptyText(): string {
    return this.data.selector.options.getValue().length == 0 ? 'Nothing to show' : 'No results matching filter';
  }

  getOptionTitle(option: T): string {
    const secondaryValue: string = this.data.getSecondaryDisplayValue ? this.data.getSecondaryDisplayValue(option) : null;
    return this.data.getPrimaryDisplayValue(option) + (secondaryValue ? ' (' + secondaryValue + ')'  : '');
  }

  getDialogContainerStyles(): string {
    return (this.data && this.data.minWidth) ? ('min-width:' + this.data.minWidth + ';') : '';
  }

  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: any) {
    if(this.dialog.openDialogs?.length > 0 && this.dialog.openDialogs[this.dialog.openDialogs.length - 1] != this.dialogRef)
      return;
    switch(event.key) {
      case 'Enter':
        event.preventDefault();
        this.confirm();
        break;
    }
  }
}

export interface ListSearchSelectDialogData<T> {
  getPrimaryDisplayValue: (option: T) => string;
  selector: OptionSelect<T>;
  loadingSubject?: BehaviorSubject<boolean>,
  filter: (options: T[], filterText: string) => T[];
  confirmText?: string;
  cancelText?: string;
  getSecondaryDisplayValue?: (option: T) => string;
  minWidth?: string;
  normalizeText?: (text: string) => string;
  searchPlaceholder?: string;
}

export interface ListSearchSelectDialogResult<T> {
  value: T;
}

export class OptionSelect<T> {
  private selectedOption: T;
  private optionsEqualFunction: (a: T, b: T) => boolean;
  options: BehaviorSubject<T[]>;
  optionSelected: Subject<SelectedOptionChange<T>>;

  constructor(options?: T[], optionsEqual?: (a: T, b: T) => boolean) {
    this.options = new BehaviorSubject<T[]>([]);
    this.selectedOption = null;
    this.optionsEqualFunction = optionsEqual || ((a: T, b: T): boolean => a == b);
    this.optionSelected = new Subject<SelectedOptionChange<T>>();
    this.update(options || []);
    this.select(null);
  }

  update(options: T[]) {
    this.options.next(options);
    if(this.getSelected() != null && options.findIndex((option: T): boolean => this.isSelected(option)) < 0)
      this.select(null);
  }

  select(option: T) {
    const oldValue: T = this.getSelected();
    this.selectedOption = option;
    this.optionSelected.next({ oldValue: oldValue, newValue: this.getSelected() });
  }

  getSelected(): T {
    return this.selectedOption;
  }

  isSelected(option: T): boolean {
    return this.optionsEqual(this.getSelected(), option);
  }

  optionsEqual(a: T, b: T): boolean {
    if(a == null)
      return b == null;
    return b != null && this.optionsEqualFunction(a, b);
  }
}

export interface SelectedOptionChange<T> {
  oldValue: T;
  newValue: T;
}
